
import { ClockCircleOutlined, CloseOutlined, DownOutlined, FilterOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Select, TimePicker } from 'antd';
import {
  filterData,
  setFilterData,
} from 'common/commonRedux/feature/filteringSlice';
import { useGetDataQuery } from 'common/commonRedux/getRTKApi/baseApi';
import { DateSelect } from 'common/components';
import { SearchIcon } from 'common/customIcons';
import { useDepartment } from 'common/hooks';
import { useAllClients } from 'common/hooks/index';
import useProjects from 'common/hooks/useProjects';
import { RootState } from 'common/redux/store';
import dayjs, { Dayjs } from 'dayjs';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import SingleDateSelect from '../DateSelect/SingleDateSelect';
import './FilteringTopBar.css';

type TabItem = { key: string; label: string };


declare global {
  interface Window {
    __startsWithMatchesCache?: { [key: string]: Set<string> };
  }
}

type TFilteringTopBar = {
  isFilterIcon?: boolean;
  isDateShow?: boolean;
  isSingleDateSelect?: boolean;
  isOnlyMonth?: boolean;
  isSingleDay?: boolean;
  // isDevice?: boolean;
  isDepartment?: boolean;
  isTopSupervisor?: boolean;
  isSearch?: boolean;
  isFilterWidthFit?: boolean;
  isEmployee?: boolean;
  isPermissionSet?: boolean;
  backgroundColor?: string;
  isProjectDateRange?: boolean;
  isProjectStatus?: boolean;
  searchPlaceholder?: string;
  searchWithButton?: boolean;
  onCollapseAll?: () => void;
  onExpandAll?: () => void;
  allCollapsed?: boolean;
  tabs?: TabItem[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  isTimeRange?: boolean;
  timeRange?: [Dayjs, Dayjs] | null;
  onTimeRangeChange?: (range: [Dayjs, Dayjs] | null) => void;
  extraFilter?: ReactNode;
  isProjectMilestoneTask?: boolean;
  isAdvanceFilter?: boolean;
  defaultAdvanceOpen?: boolean;
};
const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timer: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};
export const FilteringTopBar = ({
  isFilterIcon = true,
  backgroundColor = "transparent",
  isDateShow = true,
  isSingleDateSelect = false,
  isOnlyMonth = false,
  // isDevice = true,
  isDepartment = true,
  isTopSupervisor = false,
  isSearch = true,
  isFilterWidthFit = false,
  isEmployee = true,
  isPermissionSet = false,
  isProjectDateRange = false,
  isProjectStatus = false,
  searchPlaceholder,
  searchWithButton = false,
  onCollapseAll,
  onExpandAll,
  allCollapsed = false,
  tabs,
  activeTab,
  onTabChange,
  isTimeRange = false,
  timeRange,
  onTimeRangeChange,
  extraFilter,
  isProjectMilestoneTask = false,
  isAdvanceFilter = false,
  defaultAdvanceOpen = false,
}: TFilteringTopBar) => {
  const { t } = useTranslation();
  const themeMode = useSelector((state: RootState) => state?.theme?.themeMode);
  const isDark = themeMode === 'dark';
  const [toggle, setToggle] = useState(false);
  const getFilterData = useSelector(filterData);
  // Auto-expand the Advance filter when a project / milestone / task is already
  // selected (e.g. arriving from Task Monitoring with a preset scope).
  const hasAdvanceFilterValue = Boolean(
    getFilterData?.projectId || getFilterData?.milestoneId || getFilterData?.taskId
  );
  const [advanceOpen, setAdvanceOpen] = useState(defaultAdvanceOpen || hasAdvanceFilterValue);
  // Only auto-open once, so the user can still collapse it afterwards.
  const didAutoOpenAdvance = useRef(hasAdvanceFilterValue);
  const defaultTimeRange = useMemo<[Dayjs, Dayjs]>(() => [
    dayjs().hour(9).minute(0).second(0),
    dayjs().hour(18).minute(0).second(0),
  ], []);
  const { departments } = useDepartment();
  const { projectsDropdown } = useProjects();
  // Refs/state for the animated sliding pill behind the active tab
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [tabIndicator, setTabIndicator] = useState<{ left: number; top: number; width: number; height: number }>(
    { left: 0, top: 0, width: 0, height: 0 }
  );

  // const { designations } = useDesignations();
  const { clients } = useAllClients();
  const location = useLocation();
  const [form] = Form.useForm();
  const { data: permissionSet } = useGetDataQuery({ urlObjKey: "GetPermissions", params: {} })
  const { data: milestones } = useGetDataQuery({ urlObjKey: "GetMilestonesDropdown", paramId: getFilterData?.projectId || "0", params: {} });
  const { data: tasks } = useGetDataQuery({ urlObjKey: "GetTasksDropdown", params: {MilestoneId: getFilterData?.milestoneId || 0} });
  const taskOptions = tasks?.map((m: any) => ({
    label: m.taskName,
    value: m.taskId,
  })) || [];
  // console.log("tasks :", taskOptions)
  const milestoneOptions = milestones?.map((m: any) => ({
    label: m.name,
    value: m.milestoneId,
  })) || [];
  
    useEffect(() => {
    if (!tabs || tabs.length === 0 || !activeTab) return;
    const idx = tabs.findIndex((tab) => tab.key === activeTab);
    const node = tabRefs.current[idx];
    if (node) {
      setTabIndicator({
        left: node.offsetLeft,
        top: node.offsetTop,
        width: node.offsetWidth,
        height: node.offsetHeight,
      });
    }
  }, [tabs, activeTab]);

  // Open the Advance filter the first time a project / milestone / task scope
  // appears (it can arrive after mount once the Redux filter is populated).
  useEffect(() => {
    if (!didAutoOpenAdvance.current && hasAdvanceFilterValue) {
      didAutoOpenAdvance.current = true;
      setAdvanceOpen(true);
    }
  }, [hasAdvanceFilterValue]);

  const departmentOptions = departments?.map((dept) => ({
    label: dept.strDepartmentName,
    value: dept.intDepartmentId,
  }));


  const clientOptions = clients?.map((data: any) => ({
    label: data.employeeName,
    value: data.employeeId,
  }));
  const permissionSetOptions = permissionSet
    ?.filter((i: any) => i.strName !== "Default")
    ?.map((data: any) => ({
      label: data.strName,
      value: data.strName || '',
    }));
  const dispatch: any = useDispatch();
  const employeesPath = location.pathname.includes(
    '/employee-management/employees'
  );
  const report = location.pathname.includes('/reports/attendance');

  const handleFinished = (values: any) => {
    dispatch(setFilterData({ ...values, pageNumber: 1 }));
  };
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleSearch = useCallback(
    debounce((value: string) => {
      handleFinished({ searchValue: value.trim() });
    }, 700),
    [handleFinished]
  );

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const projectMilestoneTaskFilters = (
    <>
      <Form.Item noStyle shouldUpdate>
        {() => (
          <Select
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            placeholder={t('monitoring:filter:projectsName')}
            value={getFilterData?.projectId || undefined}
            onChange={(key, data: any) => {
              if (!key) {
                handleFinished({
                  projectName: undefined,
                  projectId: undefined,
                  milestoneName: undefined,
                  milestoneId: undefined,
                  taskName: undefined,
                  taskId: undefined,
                });
              } else {
                handleFinished({
                  projectName: data?.label,
                  projectId: data?.value,
                  milestoneName: undefined,
                  milestoneId: undefined,
                  taskName: undefined,
                  taskId: undefined,
                });
              }
            }}
            className="!min-w-[100px] max-w-[180px] w-full !text-xs"
            popupMatchSelectWidth={false}
            popupClassName="filtering-top-bar-dropdown"
            size="middle"
            options={[{ value: "", label: "All Projects" }, ...projectsDropdown]}
          />
        )}
      </Form.Item>

      {/* <Form.Item noStyle shouldUpdate>
        {() => (
          <Select
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            value={getFilterData?.milestoneId || undefined}
            onChange={(key, data: any) => {
              if (!key) {
                handleFinished({
                  milestoneName: undefined,
                  milestoneId: undefined,
                  taskName: undefined,
                  taskId: undefined,
                });
              } else {
                handleFinished({
                  milestoneName: data?.label,
                  milestoneId: data?.value,
                  taskName: undefined,
                  taskId: undefined,
                });
              }
            }}
            className="!min-w-[100px] max-w-[180px] w-full !text-xs"
            popupMatchSelectWidth={false}
            popupClassName="filtering-top-bar-dropdown"
            size="middle"
            placeholder={t('monitoring:filter:allMilestones')}
            options={[{ value: "", label: "All Milestones" }, ...milestoneOptions]}
          />
        )}
      </Form.Item> */}

      <Form.Item noStyle shouldUpdate>
        {() => (
          <Select
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            value={getFilterData?.taskId || undefined}
            onChange={(key, data: any) => {
              if (!key) {
                handleFinished({
                  taskName: undefined,
                  taskId: undefined,
                });
              }
              handleFinished({
                taskName: data?.label,
                taskId: data?.value,
              });
            }}
            className="!min-w-[100px] max-w-[180px] w-full !text-xs"
            popupMatchSelectWidth={false}
            popupClassName="filtering-top-bar-dropdown"
            size="middle"
            placeholder={t('monitoring:filter:allTasks')}
            options={[{ value: "", label: "All Tasks" }, ...taskOptions]}
            
          />
        )}
      </Form.Item>
    </>
  );

  return (
    <>
      <Card
        style={{
          backgroundColor: backgroundColor ? backgroundColor : (isDark ? '#091320' : '#F4F5F7'),
          padding: 0,
          border: 'none'
        }}
        bodyStyle={{
          padding: "12px 0px 0px 0px"
        }}
        className={` filtering-top-bar h-full ${isFilterWidthFit ? 'w-fit' : 'w-full'}`}
      >
        <Button
          onClick={() => setToggle(!toggle)}
          type="default"
          icon={toggle ? <CloseOutlined /> : <FilterOutlined />}
          className="md:hidden flex items-center gap-2 !border-primaryColor text-primaryColor"
        >
          {toggle ? t('monitoring:filter:toggle:hideFilters') : t('monitoring:filter:toggle:seeFilters')}
        </Button>
        <Form
          style={{ margin: 0 }}
          form={form}
          className={`${toggle ? 'flex' : 'hidden md:flex'} flex-col gap-3`}
        >
          <div className="flex flex-col md:flex-row justify-between gap-4 items-start w-full">
          <div className="flex flex-col md:flex-row items-center gap-2 flex-wrap">
            {/* TAB SWITCHER — segmented buttons on the left (e.g. Snapshot / Screen Record) */}
            {tabs && tabs.length > 0 && (
              <div
                className="relative flex items-center gap-1 p-[3px] rounded-[6px]"
                style={{ backgroundColor: isDark ? '#0F1E33' : '#F1F5F9' }}
              >
                {/* Sliding pill that animates between the active tabs */}
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: tabIndicator.left,
                    top: tabIndicator.top,
                    width: tabIndicator.width,
                    height: tabIndicator.height,
                    backgroundColor: '#1B73E8',
                    borderRadius: 4,
                    transition:
                      'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), top 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    pointerEvents: 'none',
                    opacity: tabIndicator.width ? 1 : 0,
                  }}
                />
                {tabs.map((tab, idx) => {
                  const isActive = tab.key === activeTab;
                  return (
                    <button
                      key={tab.key}
                      ref={(el) => {
                        tabRefs.current[idx] = el;
                      }}
                      type="button"
                      onClick={() => onTabChange?.(tab.key)}
                      className="cursor-pointer"
                      style={{
                        position: 'relative',
                        zIndex: 1,
                        padding: '6px 4px',
                        borderRadius: 4,
                        border: 'none',
                        fontSize: 10,
                        fontWeight: 500,
                        lineHeight: 1,
                        height: 28,
                        backgroundColor: 'transparent',
                        color: isActive ? '#FFFFFF' : (isDark ? '#CBD5E1' : '#64748B'),
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            )}
            {/* SEARCH INPUT — free-text employee search box (with optional inline search button) */}
            {isFilterIcon && isSearch && !report && (
              <div className="w-full md:w-[160px]">
                <Form.Item noStyle shouldUpdate>
                  {() => {
                    const formData = form.getFieldsValue(true);
                    const placeholder =
                      searchPlaceholder || t('monitoring:filter:search');
                    return (
                      <Input
                        placeholder={placeholder}
                        suffix={
                          searchWithButton ? (
                            <button
                              type="button"
                              aria-label="Search"
                              className="flex items-center justify-center rounded-md"
                              style={{
                                width: 22,
                                height: 22,
                                backgroundColor: '#1B73E8',
                                border: 'none',
                                cursor: 'pointer',
                              }}
                         
                              onClick={() =>
                                handleSearch(formData?.searchValue || '')
                              }
                            >
                              <SearchIcon style={{ color: '#FFFFFF', width: 11, height: 11 }} />
                            </button>
                          ) : (
                            <SearchIcon />
                          )
                        }
                        value={formData?.searchValue || ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          form.setFieldsValue({ searchValue: value });
                          handleSearch(value);
                        }}
                        className={`w-full !h-[30px] !rounded-[4px] ${searchWithButton ? 'pr-1' : ''}`}
                      />
                    );
                  }}
                </Form.Item>
              </div>
            )}
            {/* EMPLOYEE STATUS DROPDOWN — only on /employee-management/employees: filter by Active / Inactive */}
            {employeesPath && (
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Select
                    placeholder={t('monitoring:filter:status')}
                    value={getFilterData?.employeeStatus || undefined}
                    onChange={(key, data: any) => {
                      if (!key) {
                        handleFinished({ employeeStatus: undefined });
                      }
                      handleFinished({ employeeStatus: data?.value });
                    }}
                    allowClear
                    showSearch
                    filterOption={(input, option) => {
                      const label = (option?.label ?? '').toLowerCase();
                      const search = input.toLowerCase();

                      // Static cache to remember last search and startsWith matches
                      if (!window.__startsWithMatchesCache) {
                        window.__startsWithMatchesCache = {};
                      }

                      const matches = label.startsWith(search);

                      if (matches) {
                        // Add label to startsWith cache for current search
                        if (!window.__startsWithMatchesCache[search]) {
                          window.__startsWithMatchesCache[search] = new Set();
                        }
                        window.__startsWithMatchesCache[search].add(label);
                      }

                      // If any startsWith matches exist, allow only those
                      const startsWithSet =
                        window.__startsWithMatchesCache[search];
                      if (startsWithSet && startsWithSet.size > 0) {
                        return startsWithSet.has(label);
                      }

                      // Fallback: includes
                      return label.includes(search);
                    }}
                    style={{ height: 30, borderRadius: 4, borderColor: '#E5E7EB' }}
                    className="text-gray-500 md:w-[167px] w-full"
                    suffixIcon={<span style={{ color: '#BBBABA' }}>▼</span>}
                    options={[
                      { label: t('monitoring:filter:active'), value: 'active' },
                      { label: t('monitoring:filter:inactive'), value: 'inactive' },
                      // { label: 'Active', value: 'active' },
                      // { label: 'Inactive', value: 'inactive' },
                    ]}
                  />
                )}
              </Form.Item>
            )}

            {/* EMPLOYEE DROPDOWN — pick a specific employee (clientName / clientIdString) from the all-clients list */}
            {isFilterIcon && isEmployee && (
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Select
                    allowClear
                    showSearch
                    filterOption={(input, option) => {
                      const label = (option?.label ?? '').toLowerCase();
                      const search = input.toLowerCase();

                      window.__startsWithMatchesCache ??= {};

                      const matches = label.startsWith(search);

                      if (matches) {
                        if (!window.__startsWithMatchesCache[search]) {
                          window.__startsWithMatchesCache[search] = new Set();
                        }
                        window.__startsWithMatchesCache[search].add(label);
                      }

                      const startsWithSet =
                        window.__startsWithMatchesCache[search];
                      if (startsWithSet && startsWithSet.size > 0) {
                        return startsWithSet.has(label);
                      }

                      return label.includes(search);
                    }}
                    placeholder={t('monitoring:filter:employees')}
                    value={getFilterData?.clientName || undefined}
                    onChange={(key, data: any) => {
                      if (!key) {
                        handleFinished({
                          clientName: undefined,
                          clientIdString: undefined,
                        });
                      }
                      handleFinished({
                        clientName: data?.label,
                        clientIdString: data?.value,
                      });
                    }}
                    style={{ height: 30, borderRadius: 4, borderColor: '#E5E7EB' }}
                    className="text-gray-500 md:w-[120px] w-full"
                    suffixIcon={<span style={{ color: '#BBBABA' }}>▼</span>}
                    options={clientOptions}
                    popupClassName="filtering-top-bar-dropdown"
                  />
                )}
              </Form.Item>
            )}

            {/* DEPARTMENT DROPDOWN — filter by department (departmentName / departmentId), hidden on Reports/Attendance */}
            {isFilterIcon && isDepartment && !report && (
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Select
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .startsWith(input.toLowerCase())
                    }
                    placeholder={t('monitoring:filter:department')}
                    value={getFilterData?.departmentName || undefined}
                    style={{ height: 30, borderRadius: 4, borderColor: '#E5E7EB' }}
                    className="text-gray-500 md:w-[120px] w-full"
                    suffixIcon={
                      <span style={{ color: 'secondaryColor' }}>▼</span>
                    }
                    popupClassName="filtering-top-bar-dropdown"
                    options={departmentOptions}
                    onChange={(key, data: any) => {
                      if (!key) {
                        handleFinished({
                          departmentName: undefined,
                          departmentId: undefined,
                        });
                      }
                      handleFinished({
                        departmentName: data?.label,
                        departmentId: data?.value,
                      });
                    }}
                  />
                )}
              </Form.Item>
            )}

            {/* DATE FILTER — single-day picker (SingleDateSelect) or date-range picker (DateSelect, optionally project-range / month-only) */}
            {isDateShow && (
              <div className={`${!isFilterIcon ? 'hidden' : ''} w-full ${isSingleDateSelect ? 'md:w-[100px]' : 'md:w-[170px]'}`}>
                {isSingleDateSelect ? (
                  <div className="w-full">
                    <SingleDateSelect />
                  </div>
                ) : (
                  <div className={`${!isDateShow && 'hidden'} w-full`}>
                    <DateSelect isProjectDateRange={isProjectDateRange} isOnlyMonth={isOnlyMonth} />
                  </div>
                )}
              </div>
            )}
            {/* TIME RANGE PICKER — start/end time (e.g. 12:30pm - 4:30pm) for screen activity */}
            {isFilterIcon && isTimeRange && (
              <div className="w-32">
                <TimePicker.RangePicker
                  value={getFilterData?.timeRange ?? timeRange ?? defaultTimeRange}
                  onChange={(value) => {
                    handleFinished({ timeRange: value });
                    onTimeRangeChange?.(value as [Dayjs, Dayjs] | null);
                  }}
                  format="h:mma"
                  use12Hours
                  needConfirm={true}
                  allowClear={true}
                  popupClassName="time-range-with-headers filtering-top-bar-dropdown"
                  suffixIcon={<ClockCircleOutlined style={{ color: '#64748B' }} />}
                  style={{ height: 30, width: '100%', borderRadius: 4, borderColor: '#E5E7EB' }}
                  className="text-gray-500"
                />
              </div>
            )}
            {/* EXTRA FILTER SLOT — caller-provided custom filter (e.g. ScreenActivity passes the Emailing/Social/Chat/Meeting "All" dropdown here) */}
            {isFilterIcon && extraFilter}
            {/* TOP-N DROPDOWN — "Select Top" 5/10/20/30/50/All used by supervisor leaderboards */}
            {isFilterIcon && isTopSupervisor && !report && (
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <div className='flex items-center gap-2'>
                    <span className='text-nowrap'>{t('monitoring:filter:selectTop')}:</span>
                    <Select
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .startsWith(input.toLowerCase())
                      }
                      placeholder={t('monitoring:filter:selectTop')}
                      value={getFilterData?.topValue || 10}
                      style={{ height: 30, borderRadius: 4, borderColor: '#E5E7EB' }}
                      className="text-gray-500 md:w-[167px] w-full"
                      suffixIcon={
                        <span style={{ color: 'secondaryColor' }}>▼</span>
                      }
                      options={[
                        { label: "5", value: 5 },
                        { label: "10", value: 10 },
                        { label: "20", value: 20 },
                        { label: "30", value: 30 },
                        { label: "50", value: 50 },
                        { label: "All", value: 1000 },

                      ]}
                      onChange={(key, data: any) => {
                        if (!key) {
                          handleFinished({
                            topValue: undefined,
                          });
                        }
                        handleFinished({
                          topValue: data?.value,
                        });
                      }}
                    />
                  </div>
                )}
              </Form.Item>
            )}
            {/* PERMISSION SET DROPDOWN — pick a permission set (excludes "Default") for permission-related screens */}
            {isFilterIcon && isPermissionSet && !report && (
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      String(option?.label ?? '')
                        .toLowerCase()
                        .startsWith(input.toLowerCase())
                    }
                    placeholder={t('monitoring:filter:selectPermissionSet')}
                    value={getFilterData?.permissionSetValue || undefined}
                    style={{ height: 30, borderRadius: 4, borderColor: '#E5E7EB' }}
                    className="text-gray-500 md:w-[167px] w-full"
                    suffixIcon={
                      <span style={{ color: 'secondaryColor' }}>▼</span>
                    }
                    allowClear
                    options={permissionSetOptions || []}
                    onChange={(key, data: any) => {
                      if (!key) {
                        handleFinished({
                          permissionSetValue: undefined,
                        });
                      }
                      handleFinished({
                        permissionSetValue: data?.value,
                      });
                    }}
                  />
                )}
              </Form.Item>
            )}
            {/* PROJECT STATUS DROPDOWN — In Progress / Completed / Editing for project listings */}
            {isFilterIcon && isProjectStatus && !report && (
              <Form.Item noStyle shouldUpdate>
                {() => (
                  <Select
                    placeholder={t('monitoring:filter:projectStatus')}
                    value={getFilterData?.projectStatus || undefined}
                    style={{ height: 30, borderRadius: 4, borderColor: '#E5E7EB' }}
                    className="text-gray-500 md:w-[167px] w-full"
                    suffixIcon={
                      <span style={{ color: 'secondaryColor' }}>▼</span>
                    }
                    allowClear
                    options={[
                      { label: t('monitoring:filter:inProgress'), value: "In Progress" },
                      { label: t('monitoring:filter:completed'), value: "Completed" },
                      { label: t('monitoring:filter:editing'), value: "Editing" },
                    ]}
                    onChange={(key, data: any) => {
                      if (!key) {
                        handleFinished({
                          projectStatus: undefined,
                        });
                      }
                      handleFinished({
                        projectStatus: data?.value,
                      });
                    }}
                  />
                )}
              </Form.Item>
            )}


            {/*  Projects / Milestone / Task Dropdowns — inline when caller doesn't use the Advance toggle */}
            {isProjectMilestoneTask && !isAdvanceFilter && projectMilestoneTaskFilters}
          </div>

          {/* RIGHT-SIDE ACTIONS — Collapse/Expand toggle + Advance toggle */}
          {(onCollapseAll || onExpandAll || isAdvanceFilter) && (
            <div className="flex items-center gap-2">
              {(onCollapseAll || onExpandAll) && (
              <Button
                type="default"
                onClick={() => {
                  if (allCollapsed) {
                    onExpandAll?.();
                  } else {
                    onCollapseAll?.();
                  }
                }}
                icon={
                  allCollapsed ? (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 1.5489C4 1.5489 2.01687 1.37097 1.69391 1.69393C1.37095 2.01688 1.54891 4 1.54891 4"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 10.4511C4 10.4511 2.01687 10.629 1.69391 10.3061C1.37095 9.9831 1.54891 8 1.54891 8"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 1.5489C8 1.5489 9.98315 1.37097 10.3061 1.69393C10.629 2.01688 10.4511 4 10.4511 4"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 10.4511C8 10.4511 9.98315 10.629 10.3061 10.3061C10.629 9.9831 10.4511 8 10.4511 8"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7.00391 4.99948L10.0298 1.97363"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4.99896 7.00098L1.82031 10.1901"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4.99963 5.00144L1.92383 1.92969"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6.99023 7.00098L10.2644 10.2489"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.11397 8.99759C7.10872 8.62259 6.85112 7.38084 7.11437 7.11759C7.37767 6.85439 8.61902 7.11259 8.99387 7.11799M10.4989 10.4993L7.30647 7.30754"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4.8849 8.99709C4.89015 8.62214 5.14775 7.38039 4.8845 7.11709C4.62124 6.85389 3.37988 7.11214 3.00502 7.11749M1.5 10.4988L4.69239 7.30709"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3.00285 4.88042C3.37772 4.88521 4.61949 5.14136 4.88234 4.87768C5.14517 4.61402 4.88545 3.37266 4.87961 2.99772M4.6876 4.68347L1.5 1.50098"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8.99494 4.88042C8.62009 4.88521 7.37834 5.14136 7.11544 4.87768C6.85264 4.61402 7.11234 3.37266 7.11819 2.99772M7.31019 4.68347L10.4978 1.50098"
                        stroke="currentColor"
                        strokeWidth="0.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )
                }
                className="h-[30px] w-[120px] px-3 rounded-[4px] !border-primaryColor !text-primaryColor font-medium flex items-center justify-center"
              >
                {allCollapsed ? t('monitoring:filter:allExpand') : t('monitoring:filter:allCollapse')}
              </Button>
              )}
              {isAdvanceFilter && (
                <Button
                  type="default"
                  onClick={() => setAdvanceOpen((prev) => !prev)}
                  icon={advanceOpen ? <UpOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
                  iconPosition="end"
                  className="h-[30px] px-3 rounded-[4px] !border-[#E5E7EB] !text-gray-600 font-medium flex items-center justify-center"
                >
                  {t('monitoring:filter:advance')}
                </Button>
              )}
            </div>
          )}
          </div>

          {/* ADVANCE FILTER ROW — Project / Milestone / Task dropdowns toggled by the Advance button */}
          {isAdvanceFilter && advanceOpen && (
            <div className="flex flex-col md:flex-row items-center gap-2 flex-wrap">
              {projectMilestoneTaskFilters}
            </div>
          )}
        </Form>
      </Card>
    </>
  );
};
